import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { 
  ArrowRight,
  Check,
  Star,
  Users,
  Clock,
  Truck,
  Shield,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Homepage = () => {
  const stats = [
    { number: '10 Year', label: 'Structural Warranty' },
    { number: 'Family', label: 'Owned Since 2018' },
    { number: '2000+', label: 'Shutters Installed' },
    { number: '7 Days', label: 'Open For Appointments' }
  ];

  const materials = [
    {
      name: 'Budget',
      description: 'Quality shutters at an affordable price',
      tag: 'Best Value'
    },
    {
      name: 'Polymer',
      description: 'Market leading water resistance for wet areas',
      tag: 'For Wet Areas'
    },
    {
      name: 'Paulownia+',
      description: 'Hardwood shutters encased in a hard wearing polymer casing',
      tag: 'Most Popular'
    },
    {
      name: 'Basswood',
      description: 'Premium hardwood with superior finish',
      tag: 'Premium'
    },
    {
      name: 'UK Made Basswood',
      description: 'Premium UK manufactured basswood, 4-5 week lead time',
      tag: 'UK Made'
    },
    {
      name: 'Aluminium',
      description: 'T6 Aircraft Grade for security & external use',
      tag: 'Security'
    }
  ];

  const features = [
    '10 Year Structural Warranty',
    'Family Owned & Operated',
    'Free home consultation',
    'Professional installation'
  ];

  return (
    <div className="min-h-screen bg-bone font-body -mt-20 md:-mt-24 lg:-mt-28">
      {/* Hero Section - Full viewport, asymmetric layout */}
      <section className="relative min-h-[90vh] flex items-center pt-20 md:pt-24 lg:pt-28">
        {/* Background Image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_carpentry-site/artifacts/qee3zm7j_03.%20Taupe%20Bay%20Shutters%20-%20Open.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/60 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 w-full">
          <div className="max-w-2xl">
            {/* Small tag */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-sage-300 text-sm font-medium tracking-widest uppercase mb-6"
            >
              Family Owned Since 2018
            </motion.p>
            
            {/* Main heading */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-6"
            >
              Bespoke Shutters,{' '}
              <span className="text-sage-400">Crafted</span> for Your Home
            </motion.h1>
            
            {/* Subheading */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-white/80 mb-8 leading-relaxed"
            >
              Made to measure plantation shutters across Bristol, Somerset, 
              Gloucestershire, Wiltshire & South Wales. Expert fitting with a 
              10 year structural warranty.
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/calculator">
                <Button 
                  size="lg" 
                  className="bg-sage-400 hover:bg-sage-500 text-white rounded-full px-8 py-6 text-base font-medium shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-get-quote-btn"
                >
                  Use Our Online Estimator
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/shutters/materials">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium backdrop-blur-sm"
                  data-testid="hero-view-range-btn"
                >
                  View Our Range
                </Button>
              </Link>
            </motion.div>

            {/* Trust indicators */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap gap-6 mt-12 text-white/70 text-sm"
            >
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-sage-400" />
                  <span>{feature}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-stone-200">
            {stats.map((stat, i) => (
              <div key={i} className="py-8 px-6 text-center">
                <p className="font-display text-3xl md:text-4xl text-slate-850 mb-1">{stat.number}</p>
                <p className="text-sm text-stone-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Bento Grid */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="max-w-2xl mb-16">
            <p className="text-sage-500 text-sm font-medium tracking-widest uppercase mb-4">Why Choose Us</p>
            <h2 className="font-display text-4xl md:text-5xl text-slate-850 mb-6">
              A family business built on trust
            </h2>
            <p className="text-lg text-stone-600">
              Three generations of expertise, delivering quality shutters with personal care 
              that only a family business can provide.
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large Feature Card */}
            <div className="md:col-span-7 bg-white rounded-3xl p-8 md:p-12 border border-stone-100 shadow-soft group hover:shadow-lift transition-all">
              <div className="flex items-start justify-between mb-8">
                <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center">
                  <Shield className="h-7 w-7 text-sage-500" />
                </div>
                <span className="text-xs font-medium text-sage-600 bg-sage-50 px-3 py-1 rounded-full">10 Year Structural Warranty</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-slate-850 mb-4">
                Quality Guaranteed
              </h3>
              <p className="text-stone-600 leading-relaxed mb-6">
                Every shutter we install comes with our comprehensive 10-year structural warranty. 
                Our family reputation stands behind every product - we treat your home 
                like our own.
              </p>
              <Link to="/about" className="inline-flex items-center text-sage-600 font-medium hover:text-sage-700 transition-colors">
                Learn about our story
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Stacked Cards */}
            <div className="md:col-span-5 flex flex-col gap-6">
              <div className="bg-sage-400 rounded-3xl p-8 text-white">
                <Users className="h-8 w-8 mb-4 opacity-80" />
                <h3 className="font-display text-xl mb-2">Family Expertise</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Named after our children Aston & Lily, this business represents 
                  our commitment to family values and craftsmanship.
                </p>
              </div>
              <div className="bg-white rounded-3xl p-8 border border-stone-100 shadow-soft">
                <Clock className="h-8 w-8 mb-4 text-sage-500" />
                <h3 className="font-display text-xl text-slate-850 mb-2">Express Shipping Available</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  4-5 week lead time for timber shutters when requested (regular lead times 10-12 weeks).
                  Open 7 days a week for your convenience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
            <div className="max-w-xl">
              <p className="text-sage-500 text-sm font-medium tracking-widest uppercase mb-4">Our Range</p>
              <h2 className="font-display text-4xl md:text-5xl text-slate-850">
                Premium materials for every space
              </h2>
            </div>
            <Link to="/shutters/materials">
              <Button variant="outline" className="rounded-full border-slate-850 text-slate-850 hover:bg-slate-850 hover:text-white px-6">
                View All Materials
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Materials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {materials.map((material, i) => (
              <div 
                key={i} 
                className="group bg-bone rounded-2xl p-6 border border-stone-100 hover:border-sage-200 hover:shadow-soft transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-xs font-medium text-sage-600 bg-sage-50 px-3 py-1 rounded-full">
                    {material.tag}
                  </span>
                </div>
                <h3 className="font-display text-xl text-slate-850 mb-2">{material.name}</h3>
                <p className="text-stone-500 text-sm mb-4 leading-relaxed">{material.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-slate-850 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-sage-400 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-sage-400 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative px-8 md:px-16 py-16 md:py-20 text-center">
              <h2 className="font-display text-3xl md:text-5xl text-white mb-6">
                Ready to transform your windows?
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">
                Get an instant estimate with our online calculator, or book a free home 
                consultation with our team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/calculator">
                  <Button 
                    size="lg" 
                    className="bg-sage-400 hover:bg-sage-500 text-white rounded-full px-8 py-6 text-base font-medium"
                    data-testid="cta-get-quote-btn"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/447378415338" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                    data-testid="cta-whatsapp-btn"
                  >
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-8">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 fill-sage-400 text-sage-400" />
            ))}
          </div>
          <blockquote className="font-display text-2xl md:text-3xl text-slate-850 leading-relaxed mb-8">
            "Absolutely thrilled with our new shutters. The team were professional 
            from start to finish, and the quality is exceptional. Would highly recommend 
            to anyone looking for shutters."
          </blockquote>
          <p className="text-stone-500">
            <span className="font-medium text-slate-850">Sarah M.</span> — Bristol
          </p>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
